import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiconfig/api-config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';
import { formatDateDDMMYY } from '../common/dateUtils';

const Profile = ({ onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [updating, setUpdating] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/profile/profile-get`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setProfile(response.data.data);
        setFormData({
          name: response.data.data.name,
          email: response.data.data.email
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error(
        error.response?.data?.error || 'Failed to load profile',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      name: profile.name,
      email: profile.email
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Name and email are required', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await axios.put(
        `${API_BASE_URL}/api/profile/profile-update`,
        formData,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Profile updated successfully!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
        
        // Refresh profile data
        await fetchProfile();
        setEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(
        error.response?.data?.error || 'Failed to update profile',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        }
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      
      console.log('Starting logout process...');
      
      // Call logout API to clear server-side tokens
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );

      console.log('Logout API response:', response.data);

      // Clear any client-side stored tokens
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      
      // Clear axios default headers
      delete axios.defaults.headers.common['Authorization'];
      
      toast.success('Logged out successfully!', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });

      // Close profile panel
      if (onClose) {
        onClose();
      }

      // Redirect to login page
      setTimeout(() => {
        navigate('/');
      }, 1000);

    } catch (error) {
      console.error('Logout error:', error);
      console.error('Error response:', error.response?.data);
      
      // Even if server logout fails, clear client-side tokens
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      
      toast.error('Logout failed, but tokens cleared locally', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });

      // Still redirect to login
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } finally {
      setLogoutLoading(false);
    }
  };

  // Using the common formatDateDDMMYY function from dateUtils

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#35aceb] mx-auto"></div>
          <p className="mt-1 text-[10px] text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="text-center">
          <p className="text-[10px] text-gray-500">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer 
        toastClassName={() =>
          'relative flex items-center p-3 min-h-10 rounded-md justify-between overflow-hidden cursor-pointer bg-[#35aceb]/90 text-white text-sm font-semibold shadow-lg backdrop-blur-sm border border-[#2d9cdb]/30'
        }
      />
      
      <div className="space-y-5">
        {editing ? (
          // Edit Form
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:border-[#35aceb] focus:ring-2 focus:ring-[#35aceb]"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:border-[#35aceb] focus:ring-2 focus:ring-[#35aceb]"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-base border border-gray-300 text-gray-700 rounded hover:bg-gray-100 font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updating}
                className={`px-4 py-2 text-base text-white rounded font-semibold transition-colors duration-200 ${
                  updating 
                    ? 'bg-[#35aceb] opacity-70 cursor-not-allowed' 
                    : 'bg-[#35aceb] hover:bg-[#2d9cdb]'
                }`}
              >
                {updating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          // Display Profile
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-bold text-gray-900">User Information</h4>
              <button
                onClick={handleEdit}
                className="text-base text-[#35aceb] hover:text-[#2d9cdb] font-semibold transition-colors duration-200 border border-[#35aceb] px-3 py-1 rounded shadow-sm"
              >
                Edit
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Name
                </label>
                <p className="text-base text-gray-900 font-semibold">{profile.name}</p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Email
                </label>
                <p className="text-base text-gray-900">{profile.email}</p>
              </div>
              
              <div className="flex flex-row gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Role
                  </label>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-semibold bg-[#35aceb]/10 text-[#35aceb]">
                    {profile.role}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Status
                  </label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-semibold ${
                    profile.isActive 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-3">
              <h4 className="text-base font-semibold text-gray-700 mb-2">Account Details</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Last Login:</span>
                  <span className="text-sm text-gray-700">{formatDateDDMMYY(profile.lastLogin)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Created:</span>
                  <span className="text-sm text-gray-700">{formatDateDDMMYY(profile.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Updated:</span>
                  <span className="text-sm text-gray-700">{formatDateDDMMYY(profile.updatedAt)}</span>
                </div>
               
              </div>
            </div>

            {/* Logout Button */}
            <div className="border-t border-gray-200 pt-3">
              <button
                onClick={handleLogout}
                disabled={logoutLoading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-base text-white rounded font-bold transition-colors duration-200 tracking-wide shadow-md ${
                  logoutLoading 
                    ? 'bg-red-400 opacity-70 cursor-not-allowed' 
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                <FiLogOut className="text-xl mb-px" />
                {logoutLoading ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;
